// ── Templates Production Tool — shared data contract (section 13) ──
// Every Template Folder still lives in the EXACT SAME storage the old
// Project Templates popup already used: `consistency_demo_templates_{creatorId}`
// via `loadTemplates`/`saveTemplates` in ProjectTemplateLibraryDemo.jsx.
// Nothing here introduces a second, incompatible storage implementation —
// this file only adds typed accessors, folder/template CRUD, project
// linking, and episode snapshotting ON TOP of that one array.
import { loadTemplates, saveTemplates } from '@/components/project-templates/ProjectTemplateLibraryDemo';

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
function readJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}

export { loadTemplates as loadFolders, saveTemplates as saveFolders };

// ── Template types (section 3 terminology) ──
export const TEMPLATE_TYPES = [
  { key: 'editing', label: 'Editing Template', tool: 'Main Editor', iconKey: 'clapperboard' },
  { key: 'clip', label: 'Clip Template', tool: 'Clips', iconKey: 'scissors' },
  { key: 'image_post', label: 'Image & Post Template', tool: 'Images & Posts', iconKey: 'image' },
  { key: 'thumbnail', label: 'Thumbnail Template', tool: 'Images & Posts', iconKey: 'imagePlus' },
  { key: 'optimization', label: 'Optimization Template', tool: 'Optimize', iconKey: 'sparkles' },
  { key: 'scheduling', label: 'Scheduling Template', tool: 'Schedule', iconKey: 'calendarClock' },
  { key: 'upload', label: 'Upload Preset', tool: 'Upload', iconKey: 'uploadCloud' },
  { key: 'golive', label: 'Go Live Preset', tool: 'Go Live', iconKey: 'radio' },
];
export const TEMPLATE_KIND_TO_TYPE = Object.fromEntries(TEMPLATE_TYPES.map((t) => [t.label, t.key]));
export const TYPE_LABEL = Object.fromEntries(TEMPLATE_TYPES.map((t) => [t.key, t.label]));
export const TYPE_TOOL = Object.fromEntries(TEMPLATE_TYPES.map((t) => [t.key, t.tool]));

/** Every template entry in a folder, including a synthesized one for legacy folders saved before per-type entries existed (nothing already saved is lost). */
export function entriesOf(folder) {
  if (Array.isArray(folder?.editing_templates) && folder.editing_templates.length > 0) return folder.editing_templates;
  if (folder && ((folder.segments?.length || 0) > 0 || (folder.outputs?.length || 0) > 0)) {
    return [{ id: `${folder.id}-default`, name: 'Default Edit', segments: folder.segments, outputs: folder.outputs, templateType: 'editing', legacy: true }];
  }
  return [];
}

// Pre-this-task records were all saved into ONE flat array with no
// explicit type field — every Production Tool's panel showed every
// entry regardless of which tool actually saved it. Going forward every
// entry is tagged with an explicit `templateType`; for OLD entries we
// infer the type from the same ad-hoc field each tool already used to
// build its payload, and entries that match none of those (genuinely
// ambiguous — could have been saved from Editor, Clips, or a plain
// Image & Post with no design captured) are left untyped and kept
// visible in EVERY tool, exactly as they already were — this fix never
// hides a record that was visible before it shipped.
function inferLegacyType(et) {
  if (et.templateType) return et.templateType;
  if (et.design?.kind === 'thumbnail') return 'thumbnail';
  if (et.optimization) return 'optimization';
  if (et.scheduling) return 'scheduling';
  if (et.golive) return 'golive';
  if (et.design) return 'image_post';
  return null;
}

export function getTemplatesByType(folder, type) {
  return entriesOf(folder).filter((et) => {
    const inferred = inferLegacyType(et);
    return inferred ? inferred === type : true;
  });
}

export function countTemplatesByType(folder) {
  const counts = {};
  TEMPLATE_TYPES.forEach(({ key }) => { counts[key] = getTemplatesByType(folder, key).length; });
  return counts;
}

// ── Template entries within a folder ──
export function addTemplateEntry(creatorId, folderId, type, payload = {}) {
  const { id, templateType, ...rest } = payload;
  const entry = { ...rest, id: uid(), created_at: new Date().toISOString(), templateType: type };
  const folders = loadTemplates(creatorId);
  const next = folders.map((f) => (f.id === folderId ? { ...f, editing_templates: [...entriesOf(f), entry], updated_at: new Date().toISOString() } : f));
  saveTemplates(creatorId, next);
  return { folders: next, entry };
}

export function createFolderWithTemplate(creatorId, folderPayload, type, templatePayload) {
  const { folders, folder } = createFolder(creatorId, { ...folderPayload, editing_templates: [] });
  return addTemplateEntry(creatorId, folder.id, type, templatePayload);
}

export function updateTemplateEntry(creatorId, folderId, entryId, patch) {
  const folders = loadTemplates(creatorId);
  const next = folders.map((f) => (f.id === folderId
    ? { ...f, editing_templates: entriesOf(f).map((et) => (et.id === entryId ? { ...et, ...patch, updated_at: new Date().toISOString() } : et)), updated_at: new Date().toISOString() }
    : f));
  saveTemplates(creatorId, next);
  return next;
}

export function removeTemplateEntry(creatorId, folderId, entryId) {
  const folders = loadTemplates(creatorId);
  const next = folders.map((f) => (f.id === folderId ? { ...f, editing_templates: entriesOf(f).filter((et) => et.id !== entryId), updated_at: new Date().toISOString() } : f));
  saveTemplates(creatorId, next);
  return next;
}

export function duplicateTemplateEntry(creatorId, folderId, entryId) {
  const folders = loadTemplates(creatorId);
  const folder = folders.find((f) => f.id === folderId);
  const entry = folder && entriesOf(folder).find((et) => et.id === entryId);
  if (!folder || !entry) return folders;
  const copy = { ...JSON.parse(JSON.stringify(entry)), id: uid(), name: `${entry.name} (Copy)`, created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
  const next = folders.map((f) => (f.id === folderId ? { ...f, editing_templates: [...entriesOf(f), copy], updated_at: new Date().toISOString() } : f));
  saveTemplates(creatorId, next);
  return next;
}

/** Moves a template entry into a different Template Folder (section 7) — never duplicates the folder itself, only relocates the one entry. */
export function moveTemplateEntry(creatorId, fromFolderId, toFolderId, entryId) {
  if (fromFolderId === toFolderId) return loadTemplates(creatorId);
  const folders = loadTemplates(creatorId);
  const fromFolder = folders.find((f) => f.id === fromFolderId);
  const entry = fromFolder && entriesOf(fromFolder).find((et) => et.id === entryId);
  if (!fromFolder || !entry) return folders;
  const next = folders.map((f) => {
    if (f.id === fromFolderId) return { ...f, editing_templates: entriesOf(f).filter((et) => et.id !== entryId), updated_at: new Date().toISOString() };
    if (f.id === toFolderId) return { ...f, editing_templates: [...entriesOf(f), entry], updated_at: new Date().toISOString() };
    return f;
  });
  saveTemplates(creatorId, next);
  return next;
}

// ── Template Folder CRUD ──
export function createFolder(creatorId, payload = {}) {
  const folder = {
    id: uid(), name: 'Untitled Template Folder', description: '', content_type: 'Custom', series_name: '',
    segments: [], outputs: [], use_brand_kit_defaults: false, owner: 'you', visibility: 'private',
    editing_templates: [], created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    ...payload,
  };
  const folders = loadTemplates(creatorId);
  const next = [folder, ...folders];
  saveTemplates(creatorId, next);
  return { folders: next, folder };
}

export function updateFolder(creatorId, folderId, patch) {
  const folders = loadTemplates(creatorId);
  const next = folders.map((f) => (f.id === folderId ? { ...f, ...patch, updated_at: new Date().toISOString() } : f));
  saveTemplates(creatorId, next);
  return next;
}

/** Deleting a folder only removes it (and its project links) from the library — it never touches content already produced from its templates, which lives entirely in its own storage (variants, clips, designs, scheduled posts, etc.), never inside the folder. */
export function deleteFolder(creatorId, folderId) {
  const next = loadTemplates(creatorId).filter((f) => f.id !== folderId);
  saveTemplates(creatorId, next);
  unlinkFolderFromAllProjects(folderId);
  return next;
}

export function duplicateFolder(creatorId, folderId) {
  const folders = loadTemplates(creatorId);
  const folder = folders.find((f) => f.id === folderId);
  if (!folder) return folders;
  const copy = {
    ...JSON.parse(JSON.stringify(folder)), id: uid(), name: `${folder.name} (Copy)`,
    editing_templates: entriesOf(folder).map((et) => ({ ...et, id: uid() })),
    created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
  };
  const next = [copy, ...folders];
  saveTemplates(creatorId, next);
  return next;
}

// ── Project-linked Template Folders (section 9) ──
// Additive, new localStorage key — existing project records/ids are
// never touched. A link only ever stores a folderId reference, never a
// copy of the folder's own templates.
const LINKS_KEY = (projectId) => `consistency_project_template_links_${projectId}`;

export function getProjectLinks(projectId) {
  return readJson(LINKS_KEY(projectId), { folderIds: [], defaults: {}, disabled: {} });
}
function saveProjectLinks(projectId, links) { localStorage.setItem(LINKS_KEY(projectId), JSON.stringify(links)); }

export function linkFolderToProject(projectId, folderId) {
  const links = getProjectLinks(projectId);
  if (links.folderIds.includes(folderId)) return links;
  const next = { ...links, folderIds: [...links.folderIds, folderId] };
  saveProjectLinks(projectId, next);
  return next;
}

export function unlinkFolderFromProject(projectId, folderId) {
  const links = getProjectLinks(projectId);
  const next = {
    ...links,
    folderIds: links.folderIds.filter((id) => id !== folderId),
    defaults: Object.fromEntries(Object.entries(links.defaults).filter(([, v]) => v.folderId !== folderId)),
  };
  saveProjectLinks(projectId, next);
  return next;
}

function unlinkFolderFromAllProjects(folderId) {
  // No backend project↔folder index exists — scanning this browser's own
  // link keys is the only way to find every project that referenced this
  // folder (Level 3: a real backend association would make this exact).
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('consistency_project_template_links_')) {
      const links = readJson(key, null);
      if (links && links.folderIds?.includes(folderId)) {
        unlinkFolderFromProject(key.replace('consistency_project_template_links_', ''), folderId);
      }
    }
  }
}

export function setProjectDefault(projectId, type, folderId, templateId) {
  const links = getProjectLinks(projectId);
  const next = { ...links, defaults: { ...links.defaults, [type]: { folderId, templateId } } };
  saveProjectLinks(projectId, next);
  return next;
}
export function clearProjectDefault(projectId, type) {
  const links = getProjectLinks(projectId);
  const defaults = { ...links.defaults };
  delete defaults[type];
  const next = { ...links, defaults };
  saveProjectLinks(projectId, next);
  return next;
}
export function setToolDisabledForProject(projectId, type, disabled) {
  const links = getProjectLinks(projectId);
  const next = { ...links, disabled: { ...links.disabled, [type]: disabled } };
  saveProjectLinks(projectId, next);
  return next;
}

export function getLinkedFolders(creatorId, projectId) {
  const links = getProjectLinks(projectId);
  const folders = loadTemplates(creatorId);
  return links.folderIds.map((id) => folders.find((f) => f.id === id)).filter(Boolean);
}

/** The project's configured default template for one tool type (section 10) — explicit default first, then the one obvious candidate if only one exists, so a creator is never forced to choose repeatedly. */
export function getProjectDefaultTemplate(creatorId, projectId, type) {
  const links = getProjectLinks(projectId);
  if (links.disabled?.[type]) return null;
  const folders = loadTemplates(creatorId);
  const def = links.defaults?.[type];
  if (def) {
    const folder = folders.find((f) => f.id === def.folderId);
    const entry = folder && entriesOf(folder).find((et) => et.id === def.templateId);
    if (folder && entry) return { folder, entry };
  }
  const candidates = getProjectAlternateTemplates(creatorId, projectId, type);
  return candidates.length === 1 ? candidates[0] : null;
}

/** Every template of this type across the project's linked folders — the alternates list for switching (section 12). */
export function getProjectAlternateTemplates(creatorId, projectId, type) {
  const linked = getLinkedFolders(creatorId, projectId);
  return linked.flatMap((f) => getTemplatesByType(f, type).map((et) => ({ folder: f, entry: et })));
}

// ── Episode-specific template snapshots (sections 10–12) ──
// There is no backend "episode" entity anywhere in this codebase (no
// series/episode model, no episode creation endpoint — checked
// all_models.py and every router). Per instruction, this is built around
// the existing, already-stable PROJECT + PARENT ASSET context instead of
// inventing one: a creator's real "new episode" moment is uploading a new
// video into an existing Project, so the Parent Asset's own id is used as
// the episode identity here. Level 3: a real episode/series backend model
// would let this survive a browser switch; today it is per-browser only.
const EPISODE_KEY = (projectId) => `consistency_episode_templates_${projectId}`;

export function getEpisodeTemplateState(projectId, episodeId) {
  const all = readJson(EPISODE_KEY(projectId), {});
  return all[episodeId] || {};
}
function saveEpisodeState(projectId, episodeId, state) {
  const all = readJson(EPISODE_KEY(projectId), {});
  localStorage.setItem(EPISODE_KEY(projectId), JSON.stringify({ ...all, [episodeId]: state }));
}

/** First time a tool opens for (project, episode, type) with no prior selection, freeze an editable copy of the project's current default — later edits to the master template never reach this snapshot automatically. Safe to call repeatedly; a no-op once a selection exists. */
export function ensureEpisodeSnapshot(creatorId, projectId, episodeId, type) {
  const state = getEpisodeTemplateState(projectId, episodeId);
  if (state[type]) return state[type];
  const def = getProjectDefaultTemplate(creatorId, projectId, type);
  if (!def) return null;
  const entry = {
    folderId: def.folder.id, folderName: def.folder.name, templateId: def.entry.id, templateName: def.entry.name,
    sourceVersion: def.entry.updated_at || def.entry.created_at, snapshot: JSON.parse(JSON.stringify(def.entry)),
    customized: false, selectedAt: new Date().toISOString(), isProjectDefault: true,
  };
  saveEpisodeState(projectId, episodeId, { ...state, [type]: entry });
  return entry;
}

/** Explicit switch to a different available template for this episode (section 12) — always a fresh, un-customized snapshot; never silently discards what was there (callers should confirm first if `customized` was true). */
export function selectEpisodeTemplate(projectId, episodeId, type, folder, entry, { isProjectDefault = false } = {}) {
  const state = getEpisodeTemplateState(projectId, episodeId);
  const next = {
    folderId: folder.id, folderName: folder.name, templateId: entry.id, templateName: entry.name,
    sourceVersion: entry.updated_at || entry.created_at, snapshot: JSON.parse(JSON.stringify(entry)),
    customized: false, selectedAt: new Date().toISOString(), isProjectDefault,
  };
  saveEpisodeState(projectId, episodeId, { ...state, [type]: next });
  return next;
}

/** Marks the episode's own snapshot as hand-edited. The master template this came from is never touched by this call (section 11). */
export function markEpisodeCustomized(projectId, episodeId, type, patchToSnapshot) {
  const state = getEpisodeTemplateState(projectId, episodeId);
  const current = state[type];
  if (!current) return null;
  const next = { ...current, snapshot: { ...current.snapshot, ...patchToSnapshot }, customized: true };
  saveEpisodeState(projectId, episodeId, { ...state, [type]: next });
  return next;
}

/** Whether the source master template has changed since this episode's snapshot was taken. */
export function isEpisodeTemplateStale(creatorId, episodeEntry) {
  if (!episodeEntry) return false;
  const folders = loadTemplates(creatorId);
  const folder = folders.find((f) => f.id === episodeEntry.folderId);
  const live = folder && entriesOf(folder).find((et) => et.id === episodeEntry.templateId);
  if (!live) return false;
  return (live.updated_at || live.created_at) !== episodeEntry.sourceVersion;
}

/** Explicit, creator-initiated "reapply the latest template version" — overwrites THIS episode's own customizations with the current master template. Never happens automatically (section 11). */
export function reapplyLatestTemplate(creatorId, projectId, episodeId, type) {
  const state = getEpisodeTemplateState(projectId, episodeId);
  const current = state[type];
  if (!current) return null;
  const folders = loadTemplates(creatorId);
  const folder = folders.find((f) => f.id === current.folderId);
  const live = folder && entriesOf(folder).find((et) => et.id === current.templateId);
  if (!live) return null;
  const next = { ...current, sourceVersion: live.updated_at || live.created_at, snapshot: JSON.parse(JSON.stringify(live)), customized: false };
  saveEpisodeState(projectId, episodeId, { ...state, [type]: next });
  return next;
}

/** Explicit "save this episode's current settings as a new, separate template" — never mutates the master template it started from (section 11). */
export function saveEpisodeSnapshotAsNewTemplate(creatorId, folderId, episodeEntry, name) {
  if (!episodeEntry) return null;
  return addTemplateEntry(creatorId, folderId, episodeEntry.snapshot.templateType, { ...episodeEntry.snapshot, name });
}

export function disableEpisodeTemplate(projectId, episodeId, type) {
  const state = getEpisodeTemplateState(projectId, episodeId);
  saveEpisodeState(projectId, episodeId, { ...state, [type]: null });
}

// ── Public Templates (section 6) ──
// No backend marketplace / cross-account template index exists anywhere
// in this codebase. `GET /api/creators` (already used by Creator
// Hub/Analytics) genuinely lists every creator profile the LOGGED-IN
// user owns; this scans each one's own already-local
// `consistency_demo_templates_{creatorId}` folders for `visibility:'public'`.
// This is real data this browser actually has — never another account's
// private templates, and never a fabricated platform-wide marketplace.
export async function getPublicTemplates(apiUrl, activeCreatorId) {
  try {
    const res = await fetch(`${apiUrl}/api/creators`, { credentials: 'include' });
    if (!res.ok) return [];
    const creators = await res.json();
    const results = [];
    (Array.isArray(creators) ? creators : []).forEach((c) => {
      const cid = c.id;
      if (!cid || cid === activeCreatorId) return;
      loadTemplates(cid).filter((f) => f.visibility === 'public').forEach((f) => {
        results.push({ ...f, ownerCreatorId: cid, ownerCreatorName: c.display_name || c.name || 'Another creator' });
      });
    });
    return results;
  } catch { return []; }
}

/** Copies a public folder into the viewing creator's own library — the supported path for customizing someone else's public template (section 6). The original owner's folder is untouched. */
export function copyFolderToMyLibrary(myCreatorId, publicFolder) {
  const copy = {
    ...JSON.parse(JSON.stringify(publicFolder)), id: uid(), owner: 'you', visibility: 'private',
    editing_templates: entriesOf(publicFolder).map((et) => ({ ...et, id: uid() })),
    created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
  };
  delete copy.ownerCreatorId; delete copy.ownerCreatorName;
  const folders = loadTemplates(myCreatorId);
  const next = [copy, ...folders];
  saveTemplates(myCreatorId, next);
  return { folders: next, folder: copy };
}
